'use client';

import { createContext, useCallback, useEffect, useRef } from 'react';

export const SmartCaptchaContext = createContext(null);

const SITEKEY = process.env.NEXT_PUBLIC_YANDEX_SMARTCAPTCHA_SITEKEY;
const ONLOAD_CALLBACK_NAME = '__onYandexSmartCaptchaLoad';
const SCRIPT_SRC = `https://smartcaptcha.yandexcloud.net/captcha.js?render=onload&onload=${ONLOAD_CALLBACK_NAME}`;
const TOKEN_TIMEOUT_MS = 180000; // 3 минуты — даём пользователю время реально пройти визуальный вызов

/**
 * Mounts a single invisible Yandex SmartCaptcha widget for the whole app and
 * exposes `getCaptchaToken()` (via the `useSmartCaptcha` hook) so any form can
 * request a token right before submitting.
 */
export function SmartCaptchaProvider({ children }) {
    const containerRef = useRef(null);
    const widgetIdRef = useRef(null);
    const pendingRef = useRef(null); // { resolve, reject, timeoutId } while a token request is in flight

    const handleToken = useCallback((token) => {
        const pending = pendingRef.current;
        if (!pending) return;
        pendingRef.current = null;
        clearTimeout(pending.timeoutId);
        pending.resolve(token);
        if (window.smartCaptcha && widgetIdRef.current != null) {
            window.smartCaptcha.reset(widgetIdRef.current);
        }
    }, []);

    useEffect(() => {
        if (!SITEKEY) {
            console.warn('NEXT_PUBLIC_YANDEX_SMARTCAPTCHA_SITEKEY is not set — captcha disabled');
            return;
        }

        function renderWidget() {
            if (!window.smartCaptcha || widgetIdRef.current != null || !containerRef.current) return;
            widgetIdRef.current = window.smartCaptcha.render(containerRef.current, {
                sitekey: SITEKEY,
                invisible: true,
                shieldPosition: 'bottom-left',
                callback: handleToken,
            });
        }

        if (window.smartCaptcha) {
            renderWidget();
            return;
        }

        window[ONLOAD_CALLBACK_NAME] = renderWidget;

        if (!document.querySelector('script[data-smartcaptcha]')) {
            const script = document.createElement('script');
            script.src = SCRIPT_SRC;
            script.defer = true;
            script.dataset.smartcaptcha = 'true';
            document.head.appendChild(script);
        }
    }, [handleToken]);

    const getCaptchaToken = useCallback(() => {
        if (!SITEKEY) {
            // Captcha isn't configured in this environment — don't block submission.
            return Promise.resolve(null);
        }

        return new Promise((resolve, reject) => {
            if (!window.smartCaptcha || widgetIdRef.current == null) {
                reject(new Error('Captcha is not ready yet, please try again'));
                return;
            }
            if (pendingRef.current) {
                reject(new Error('Captcha check already in progress'));
                return;
            }

            const timeoutId = setTimeout(() => {
                pendingRef.current = null;
                reject(new Error('Captcha check timed out'));
            }, TOKEN_TIMEOUT_MS);

            pendingRef.current = { resolve, reject, timeoutId };
            window.smartCaptcha.execute(widgetIdRef.current);
        });
    }, []);

    return (
        <SmartCaptchaContext.Provider value={{ getCaptchaToken }}>
            {children}
            <div ref={containerRef} />
        </SmartCaptchaContext.Provider>
    );
}

export default SmartCaptchaProvider;
